import { namedAbbreviation, shortOffset } from '@/utils/timeZones'
import { parseServerTimestamp } from '@/utils/time'
import { VIDEO_END_SLACK_SECONDS } from './limits'
import type { RoomPageConfig, RoomState, WebinarRoomVideoState } from './types'

/**
 * The room's clocks. Every time it is given is a true UTC instant (the room state and the page config both carry
 * `…Utc` values, unlike a web's wall-clock `StartDateTime`), so nothing here resolves a wall clock: it only formats
 * an instant in the event's zone.
 */

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * A countdown: "04:59" under an hour, "2:04:59" from an hour, "13 days 22 h" from a day, where a running second
 * hand would only read as noise. Rounded up, so it reads 00:01 until the moment itself.
 */
export function formatCountdown(seconds: number): string {
    const whole = Math.max(0, Math.ceil(seconds))
    const days = Math.floor(whole / 86400)
    const hours = Math.floor(whole / 3600)
    const minutes = Math.floor((whole % 3600) / 60)
    if (days > 0) {
        const dayText = `${days} ${days === 1 ? 'day' : 'days'}`
        return hours % 24 > 0 ? `${dayText} ${hours % 24} h` : dayText
    }
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(whole % 60)}` : `${pad(minutes)}:${pad(whole % 60)}`
}

/** Time left on something short, as m:ss: "0:42", "4:59". Rounded up, like `formatCountdown`, so it reads 0:01 until the moment. */
export function formatTimeLeft(seconds: number): string {
    const whole = Math.max(0, Math.ceil(seconds))
    return `${Math.floor(whole / 60)}:${pad(whole % 60)}`
}

/** A point in the session as mm:ss with the minutes uncapped: "23:14", "45:00", "75:00". Rounded down, never ahead. */
export function formatSessionClock(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds))
    return `${pad(Math.floor(whole / 60))}:${pad(whole % 60)}`
}

/**
 * A recording's length as mm:ss, rounded up to the second as the API's too-long check rounds it, so a 180.4 s
 * recording reads 03:01 here as it does on the web's Room tab. A point in it rounds down (`formatSessionClock`).
 */
export function formatRecordingClock(seconds: number): string {
    return formatSessionClock(Math.ceil(seconds))
}

/**
 * How long the stream job's last report stays the clock to run on from: the job reports every 5 s, and whoever runs
 * the room rereads it every 15 s, so theirs is never older than this while the job runs.
 */
export const POSITION_FRESH_SECONDS = 30

/**
 * Where the session is now, in seconds of the recording, or null before the recording has played. A fresh report
 * runs on to now while the stream is live and holds while it reconnects, as the viewers' picture does. Past that,
 * while live, the current run's frame 0 (`RecordingStartedUtc`) gives it: an older report may be from before a
 * restart, which carries on from where it stopped. Without either, the last report holds: a restarted run
 * that is back on the air but has not reported yet plays on from there.
 */
export function sessionClockSeconds(
    room: Pick<RoomState, 'VideoState' | 'PositionSeconds' | 'PositionReportedUtc' | 'RecordingStartedUtc' | 'RecordingDurationSeconds'>,
    nowMs: number,
): number | null {
    const live = room.VideoState === 'Live'
    const reportedMs = parseServerTimestamp(room.PositionReportedUtc)
    const frameZeroMs = parseServerTimestamp(room.RecordingStartedUtc)
    let position: number | null
    if (room.PositionSeconds != null && reportedMs !== null && nowMs - reportedMs <= POSITION_FRESH_SECONDS * 1000) {
        position = room.PositionSeconds + (live ? Math.max(0, nowMs - reportedMs) / 1000 : 0)
    } else if (live && frameZeroMs !== null) {
        position = (nowMs - frameZeroMs) / 1000
    } else {
        position = room.PositionSeconds ?? null
    }
    if (position === null) return null
    const duration = room.RecordingDurationSeconds
    return Math.max(0, duration != null ? Math.min(position, duration) : position)
}

/** The session's three moments as epoch milliseconds, or null where the room has no time set. */
export interface RoomSchedule {
    entryOpensMs: number | null
    startMs: number | null
    endMs: number | null
}

export function scheduleOfRoom(room: Pick<RoomState, 'EntryOpensUtc' | 'StartUtc' | 'EndUtc'>): RoomSchedule {
    return {
        entryOpensMs: parseServerTimestamp(room.EntryOpensUtc),
        startMs: parseServerTimestamp(room.StartUtc),
        endMs: parseServerTimestamp(room.EndUtc),
    }
}

export function scheduleOfConfig(config: Pick<RoomPageConfig, 'entryOpensUtc' | 'startUtc' | 'endUtc'>): RoomSchedule {
    return {
        entryOpensMs: parseServerTimestamp(config.entryOpensUtc),
        startMs: parseServerTimestamp(config.startUtc),
        endMs: parseServerTimestamp(config.endUtc),
    }
}

/** What the session's phase reads of the stream: Mux's state, and how far the stream job got through the recording. */
export type RoomStream = Pick<RoomState, 'VideoState' | 'PositionSeconds' | 'RecordingDurationSeconds'>

/**
 * Whether the stream ended because the recording played to its end. Mux also goes idle when an outage outlasts its
 * reconnect window, and only the job's last position tells the two apart: it reports completion within
 * `VIDEO_END_SLACK_SECONDS` of the end and a failure anywhere short of it.
 */
function videoPlayedToEnd(stream: RoomStream): boolean {
    const { PositionSeconds: position, RecordingDurationSeconds: length } = stream
    return position != null && length != null && position >= length - VIDEO_END_SLACK_SECONDS
}

/**
 * Where the session stands. The page opens its door to the room at entry, the video and chat at the start.
 *
 * Past the start the stream decides when it is known: a session that runs late after an outage stays live while
 * Mux says so, and one whose stream never came up ends at its scheduled end. A recording shorter than the slot ends
 * before it, and the session goes on to the scheduled end as live Q&A (`afterVideo`), as the API's live window does;
 * a stream that ended mid-video is still the live session, stopped, for a restart to carry on. A null stream means
 * the API could not be asked, so the schedule alone decides, as the baked page would.
 */
export type SessionPhase = 'beforeEntry' | 'holding' | 'live' | 'afterVideo' | 'ended'

export function sessionPhase(schedule: RoomSchedule, stream: RoomStream | null, nowMs: number): SessionPhase {
    if (schedule.startMs === null) return 'beforeEntry'
    if (nowMs < (schedule.entryOpensMs ?? schedule.startMs)) return 'beforeEntry'
    if (nowMs < schedule.startMs) return 'holding'
    const pastEnd = schedule.endMs !== null && nowMs >= schedule.endMs
    if (stream?.VideoState === 'Ended') {
        if (pastEnd || schedule.endMs === null) return 'ended'
        return videoPlayedToEnd(stream) ? 'afterVideo' : 'live'
    }
    const videoState = stream?.VideoState
    if (videoState === 'Live' || videoState === 'Reconnecting' || videoState === 'Starting') return 'live'
    return pastEnd ? 'ended' : 'live'
}

/** The live session, with the video playing or the Q&A after it: chat, Q&A, polls and moderation are open. */
export function isLiveSession(phase: SessionPhase): boolean {
    return phase === 'live' || phase === 'afterVideo'
}

/** What a viewer's stage shows. A replay only for a page that has one it can play (Hybrid, token from a join). */
export type ViewerStage = 'beforeEntry' | 'holding' | 'live' | 'reconnecting' | 'afterVideo' | 'ended' | 'replay'

export function viewerStage(phase: SessionPhase, videoState: WebinarRoomVideoState | null, replayPlayable: boolean): ViewerStage {
    if (phase === 'live') return videoState === 'Reconnecting' ? 'reconnecting' : 'live'
    if (phase === 'ended') return replayPlayable ? 'replay' : 'ended'
    return phase
}

/** The one word the moderator's top bar names the state with. */
export type ModeratorStatus = 'Rehearsal' | 'Live' | 'Ended'

export function moderatorStatus(phase: SessionPhase): ModeratorStatus {
    if (isLiveSession(phase)) return 'Live'
    if (phase === 'ended') return 'Ended'
    return 'Rehearsal'
}

/** Seconds from now until a moment, or null without one. */
export function secondsUntil(targetMs: number | null, nowMs: number): number | null {
    return targetMs === null ? null : (targetMs - nowMs) / 1000
}

/**
 * The event's zone if `Intl` knows it. An unknown zone throws from `Intl`, so it is dropped here and the time shows
 * in UTC, labelled so, rather than taking the page down or quietly using the reader's own zone.
 */
function knownZone(timeZone: string | null | undefined): string | null {
    if (!timeZone) return null
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone })
        return timeZone
    } catch {
        return null
    }
}

function partsIn(at: Date, zone: string, options: Intl.DateTimeFormatOptions): Record<string, string> {
    const parts: Record<string, string> = {}
    for (const part of new Intl.DateTimeFormat('en-GB', { ...options, timeZone: zone }).formatToParts(at)) parts[part.type] = part.value
    return parts
}

/** The zone's name at that moment ("BST", then "GMT" after the clocks change), or its offset where nobody names it. */
function zoneLabel(at: Date, zone: string | null): string {
    if (!zone) return 'UTC'
    return namedAbbreviation(zone, at) || shortOffset(zone, at) || zone
}

/** "3:00 PM", in the event's zone. */
function clockIn(at: Date, zone: string | null): string {
    const parts = partsIn(at, zone ?? 'UTC', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    const hour = Number(parts.hour)
    return `${hour % 12 || 12}:${parts.minute} ${hour < 12 ? 'AM' : 'PM'}`
}

/** "3:00 PM", in the event's own zone, for a line that names the zone elsewhere. */
export function formatEventClock(at: Date, timeZone: string | null | undefined): string {
    return clockIn(at, knownZone(timeZone))
}

/** "3:00 PM BST": a moment in the event's own zone, named. */
export function formatEventTime(at: Date, timeZone: string | null | undefined): string {
    const zone = knownZone(timeZone)
    return `${clockIn(at, zone)} ${zoneLabel(at, zone)}`
}

/** "Thursday 22 October", or with the year: "Thursday 22 October 2026". In the event's zone. */
export function formatEventDay(at: Date, timeZone: string | null | undefined, withYear = false): string {
    const parts = partsIn(at, knownZone(timeZone) ?? 'UTC', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    const day = `${parts.weekday} ${parts.day} ${parts.month}`
    return withYear ? `${day} ${parts.year}` : day
}

/** "Thursday 22 October 2026 · 3:00 PM to 3:45 PM BST", the door's calendar line. */
export function formatEventSpan(start: Date, end: Date | null, timeZone: string | null | undefined): string {
    const zone = knownZone(timeZone)
    const day = formatEventDay(start, timeZone, true)
    if (!end) return `${day} · ${formatEventTime(start, timeZone)}`
    return `${day} · ${clockIn(start, zone)} to ${clockIn(end, zone)} ${zoneLabel(end, zone)}`
}

/** "Dr. Helen Marsh", "Dr. Helen Marsh and Aaron Webb", "A, B and C". */
export function joinNames(names: string[]): string {
    const present = names.map(name => name.trim()).filter(Boolean)
    if (present.length <= 1) return present[0] ?? ''
    return `${present.slice(0, -1).join(', ')} and ${present[present.length - 1]}`
}
